import React, { useState } from "react";
import { FaPlus, FaTrash } from "react-icons/fa";
import CollapseSection from "./CollapseSection";
import {
  CART_PAGE_LINKS,
  MAX_CART_LINKS,
  cartDrawerSettings,
  createCartLink,
} from "../../../themes/cartDrawerSettings";
const GROUPS = [
  {
    id: "layout",
    title: "Side and colors",
    text: "Where the cart opens, and the panel colors.",
    keys: ["cartSide", "cartBackground", "cartHeadingColor", "cartTextColor", "cartIconColor", "cartLinkColor"],
  },
  {
    id: "suggestions",
    title: "Suggestions",
    text: "Products shoppers may also like, shown under the cart items.",
    keys: ["cartShowRecommendations", "cartRecommendationsTitle"],
  },
  {
    id: "items",
    title: "Cart items",
    text: "Each product row: background, name, price, and quantity.",
    keys: [
      "cartItemBackground",
      "cartItemBorder",
      "cartItemNameColor",
      "cartItemPriceColor",
      "cartItemOldPriceColor",
      "cartRemoveColor",
      "cartQtyBackground",
      "cartQtyTextColor",
    ],
  },
  {
    id: "totals",
    title: "Totals and shipping",
    text: "Subtotal, savings, and the free-shipping message. Use {amount} for the money still needed.",
    keys: [
      "cartSubtotalLabel",
      "cartSubtotalLabelColor",
      "cartSubtotalColor",
      "cartSavingsLabel",
      "cartSavingsColor",
      "cartShippingBackground",
      "cartShippingTextColor",
      "cartShippingFreeColor",
      "cartShippingBarColor",
      "cartShippingFree",
      "cartShippingUnlocked",
      "cartShippingAdd",
      "cartShippingAddColor",
    ],
  },
  {
    id: "words",
    title: "Wording",
    text: "The heading, empty cart, and the note under the total.",
    keys: ["cartHeading", "cartEmptyTitle", "cartEmptyMessage", "cartEmptyButton", "cartNote"],
  },
  {
    id: "checkout",
    title: "Checkout button",
    text: "Words, button color, text color, and outer color.",
    keys: ["cartCheckoutLabel", "cartCheckoutColor", "cartCheckoutTextColor", "cartCheckoutBorderColor"],
  },
  {
    id: "view",
    title: "Full cart button",
    text: "Words, button color, text color, and outer color.",
    keys: ["cartShowViewCart", "cartViewLabel", "cartViewColor", "cartViewTextColor", "cartViewBorderColor"],
  },
  {
    id: "show",
    title: "What to show",
    text: "Shipping progress and the savings line.",
    keys: ["cartShowShipping", "cartShowSavings"],
  },
];

function pageValue(link) {
  return CART_PAGE_LINKS.some((page) => page.value === link) ? link : "__custom__";
}

export function CartSettings({ fields, settings, renderField, menus, onMenus }) {
  const [openId, setOpenId] = useState(null);
  const byKey = Object.fromEntries((fields || []).map((field) => [field.key, field]));
  const linkPreview = (menus || []).map((item) => item.label).filter(Boolean).join(" · ");
  const toggle = (id) => setOpenId((current) => (current === id ? null : id));

  return (
    <div className="te-footer-settings">
      {GROUPS.map((group) => {
        const groupFields = group.keys.map((key) => byKey[key]).filter(Boolean);
        if (groupFields.length === 0) return null;
        const colors = cartDrawerSettings(settings);
        const fallbacks = {
          cartCheckoutColor: colors.checkoutBackground,
          cartCheckoutTextColor: colors.checkoutText,
          cartCheckoutBorderColor: colors.checkoutBorder,
          cartViewColor: colors.viewBackground,
          cartViewTextColor: colors.viewText,
          cartViewBorderColor: colors.viewBorder,
          cartIconColor: colors.iconColor,
          cartItemBackground: colors.itemBackground,
          cartItemBorder: colors.itemBorder,
          cartItemNameColor: colors.itemName,
          cartItemPriceColor: colors.itemPrice,
          cartItemOldPriceColor: colors.itemOldPrice,
          cartRemoveColor: colors.removeColor,
          cartQtyBackground: colors.qtyBackground,
          cartQtyTextColor: colors.qtyText,
          cartSubtotalLabelColor: colors.subtotalLabelColor,
          cartSubtotalColor: colors.subtotalColor,
          cartSavingsColor: colors.savingsColor,
          cartShippingBackground: colors.shippingBackground,
          cartShippingTextColor: colors.shippingText,
          cartShippingAddColor: colors.shippingAddColor,
          cartShippingFreeColor: colors.shippingFreeColor,
          cartShippingBarColor: colors.shippingBar,
          cartLinkColor: colors.linkColor,
        };
        return (
          <CollapseSection
            key={group.id}
            title={group.title}
            text={group.text}
            open={openId === group.id}
            onToggle={() => toggle(group.id)}
          >
            {groupFields.map((field) => {
              const nextField = fallbacks[field.key] ? { ...field, fallback: fallbacks[field.key] } : field;
              return <React.Fragment key={field.key}>{renderField(nextField)}</React.Fragment>;
            })}
          </CollapseSection>
        );
      })}
      <CollapseSection
        title="Extra links"
        text={linkPreview || "Optional links under the checkout button."}
        extra={`${(menus || []).length}/${MAX_CART_LINKS}`}
        open={openId === "links"}
        onToggle={() => toggle("links")}
      >
        <CartMenuEditor menus={menus} onChange={onMenus} />
      </CollapseSection>
    </div>
  );
}

function CartMenuEditor({ menus, onChange }) {
  const addLink = () => {
    if (menus.length >= MAX_CART_LINKS) return;
    onChange([...menus, createCartLink({ label: "Shipping", link: "/contact" })]);
  };

  return (
    <div className="te-footer-columns">
      {menus.length === 0 && (
        <div className="te-footer-empty">No extra links yet. Add one for shipping, returns, or contact.</div>
      )}
      {menus.map((item, index) => {
        const selectedPage = pageValue(item.link);
        return (
          <div key={item.id || index} className="te-menu-fields" style={{ marginBottom: 10 }}>
            <input
              className="te-input"
              value={item.label}
              placeholder="Link name"
              aria-label="Link name"
              onChange={(event) => {
                const next = [...menus];
                next[index] = { ...item, label: event.target.value };
                onChange(next);
              }}
            />
            <select
              className="te-select"
              aria-label="Link destination"
              value={selectedPage}
              onChange={(event) => {
                const nextValue = event.target.value;
                const next = [...menus];
                next[index] = {
                  ...item,
                  link: nextValue === "__custom__" ? "" : nextValue,
                };
                onChange(next);
              }}
            >
              {CART_PAGE_LINKS.map((page) => (
                <option key={page.value} value={page.value}>
                  {page.label}
                </option>
              ))}
              <option value="__custom__">Custom link</option>
            </select>
            {selectedPage === "__custom__" && (
              <input
                className="te-input"
                aria-label="Custom URL"
                value={item.link}
                placeholder="https://example.com or /contact"
                onChange={(event) => {
                  const next = [...menus];
                  next[index] = { ...item, link: event.target.value };
                  onChange(next);
                }}
              />
            )}
            <button
              type="button"
              className="te-footer-text-btn is-danger"
              onClick={() => onChange(menus.filter((row) => row.id !== item.id))}
            >
              <FaTrash size={10} /> Remove
            </button>
          </div>
        );
      })}
      <button type="button" className="te-add" disabled={menus.length >= MAX_CART_LINKS} onClick={addLink}>
        <FaPlus size={11} /> Add a link
      </button>
    </div>
  );
}

export function CartDrawerPreview({ settings, onClose }) {
  const look = cartDrawerSettings(settings || {});
  const checkoutStyle = {
    background: look.checkoutBackground,
    color: look.checkoutText,
    borderRadius: look.checkoutRadius,
    border: `1px solid ${look.checkoutBorder}`,
  };
  const viewStyle = {
    background: look.viewBackground,
    color: look.viewText,
    borderRadius: look.checkoutRadius,
    border: `1px solid ${look.viewBorder}`,
  };
  return (
    <div
      className={`te-cart-preview is-${look.side}`}
      role="dialog"
      aria-label={look.heading}
      onClick={onClose}
    >
      <aside
        className="te-cart-panel"
        style={{ background: look.background, color: look.headingColor }}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="te-cart-panel-head">
          <span>{look.heading} (1)</span>
          <button
            type="button"
            aria-label="Close side cart"
            onClick={onClose}
            style={{
              background: "transparent",
              border: 0,
              color: look.textColor,
              fontWeight: 600,
              fontSize: 18,
              lineHeight: 1,
              cursor: "pointer",
              padding: 0,
            }}
          >
            ×
          </button>
        </div>
        {look.showShipping && (
          <div className="te-cart-panel-head" style={{ display: "block", fontWeight: 600, fontSize: 12, color: look.shippingText, background: look.shippingBackground }}>
            <span style={{ color: look.shippingAddColor }}>{String(look.shippingAdd).replace("{amount}", "Rs. 1,100")}</span>
            <div style={{ marginTop: 6, height: 6, borderRadius: 99, background: "#e5e7eb" }}>
              <div style={{ width: "60%", height: "100%", borderRadius: 99, background: look.shippingBar }} />
            </div>
            <div style={{ marginTop: 6, color: look.shippingFreeColor }}>{look.shippingFree}</div>
          </div>
        )}
        <div className="te-cart-panel-body">
          <div className="te-cart-sample" style={{ background: look.itemBackground, border: `1px solid ${look.itemBorder}` }}>
            <div className="te-cart-sample-photo" />
            <div>
              <strong style={{ fontSize: 13, color: look.itemName }}>Sample product — M</strong>
              <div style={{ color: look.itemPrice, fontSize: 12 }}>
                Rs. 1,200 <span style={{ color: look.itemOldPrice, textDecoration: "line-through" }}>Rs. 1,300</span>
              </div>
            </div>
          </div>
        </div>
        <div className="te-cart-panel-foot" style={{ background: look.background }}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
            <span style={{ color: look.subtotalLabelColor }}>{look.subtotalLabel}</span>
            <strong style={{ color: look.subtotalColor }}>Rs. 1,200</strong>
          </div>
          {look.showSavings && (
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: look.savingsColor, marginTop: 4 }}>
              <span>{look.savingsLabel}</span>
              <span>-Rs. 100</span>
            </div>
          )}
          {look.note ? (
            <p style={{ margin: "8px 0 0", fontSize: 11, color: look.textColor }}>{look.note}</p>
          ) : null}
          <div className="te-cart-checkout" style={checkoutStyle}>
            {look.checkoutLabel} • Rs. 1,200
          </div>
          {look.showViewCart && (
            <div className="te-cart-secondary" style={viewStyle}>
              {look.viewCartLabel}
            </div>
          )}
          {look.menus.map((item) => (
            <div key={item.id} className="te-cart-link" style={{ borderRadius: look.checkoutRadius, color: look.linkColor }}>
              {item.label}
            </div>
          ))}
        </div>
      </aside>
    </div>
  );
}
